import { useState, useEffect } from 'react';
import { 
  Zap, 
  Save, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  Clock,
  MessageSquare,
  User,
  ArrowRight,
  Trash2
} from 'lucide-react';

interface EvolutionConfig {
  evolution_api_enabled: boolean;
  evolution_api_url: string;
  evolution_instance: string;
  evolution_api_key: string;
  webhook_chat_url: string;
}

interface WebhookLog {
  id: string;
  timestamp: Date;
  direction: 'incoming' | 'outgoing';
  phone: string;
  message: string;
  instance?: string;
  status?: 'success' | 'error';
  error?: string;
}

const Evolution = () => {
  const [config, setConfig] = useState<EvolutionConfig>({
    evolution_api_enabled: false,
    evolution_api_url: 'http://31.97.242.135:8080',
    evolution_instance: 'Teste',
    evolution_api_key: '99ee9taex0piir2nt6rk9',
    webhook_chat_url: ''
  });
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{success: boolean, message: string} | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load configuration from localStorage or API
    const loadConfig = async () => {
      try {
        const savedConfig = localStorage.getItem('evolutionConfig');
        if (savedConfig) {
          setConfig(JSON.parse(savedConfig));
        }

        const savedLogs = localStorage.getItem('evolutionLogs');
        if (savedLogs) {
          const parsedLogs = JSON.parse(savedLogs);
          // Convert string dates back to Date objects
          const logsWithDates = parsedLogs.map((log: any) => ({
            ...log,
            timestamp: new Date(log.timestamp)
          }));
          setLogs(logsWithDates);
        } else {
          // Sample logs
          const sampleLogs: WebhookLog[] = [
            {
              id: '1',
              timestamp: new Date(Date.now() - 3600000), // 1 hour ago
              direction: 'incoming',
              phone: '5517991082156',
              message: 'Olá, estou procurando produtos para limpeza da casa',
              instance: 'Teste',
              status: 'success'
            },
            {
              id: '2',
              timestamp: new Date(Date.now() - 3540000), // 59 minutes ago
              direction: 'outgoing',
              phone: '5517991082156',
              message: 'Olá! Sou seu assistente de compras. Como posso ajudar você hoje?',
              instance: 'Teste',
              status: 'success'
            },
            {
              id: '3',
              timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
              direction: 'incoming',
              phone: '5511987654321',
              message: 'Vocês têm protetor solar?',
              instance: 'Teste',
              status: 'success'
            }
          ];
          setLogs(sampleLogs);
          localStorage.setItem('evolutionLogs', JSON.stringify(sampleLogs));
        }
      } catch (error) {
        console.error('Error loading Evolution API configuration:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      // Save to localStorage for demo
      localStorage.setItem('evolutionConfig', JSON.stringify(config));
      
      // In a real implementation, this would be an API call
      // await fetch('/api/evolution-config', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(config)
      // });
      
      alert('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Error saving Evolution API configuration:', error);
      alert('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      // In a real implementation, this would be an actual API call
      // const response = await fetch(`${config.evolution_api_url}/instance/info/${config.evolution_instance}`, {
      //   headers: { 'apikey': config.evolution_api_key }
      // });
      
      // Simulate API response
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For demo, we'll just simulate a successful response
      setTestResult({
        success: true,
        message: 'Conexão estabelecida com sucesso! Instância está online.'
      });
    } catch (error) {
      console.error('Error testing Evolution API connection:', error);
      setTestResult({
        success: false,
        message: 'Falha ao conectar com a API. Verifique a URL e as credenciais.'
      });
    } finally {
      setTesting(false);
    }
  };

  const clearLogs = () => {
    if (window.confirm('Tem certeza que deseja limpar todos os logs?')) {
      setLogs([]);
      localStorage.setItem('evolutionLogs', JSON.stringify([]));
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Evolution API</h1>
        <p className="text-gray-600 mt-1">Configure a integração com a Evolution API para WhatsApp</p>
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Zap className="h-5 w-5 mr-2 text-blue-600" />
            Configuração da Evolution API
          </h2>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>

        <div className="space-y-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="evolution_api_enabled"
              checked={config.evolution_api_enabled}
              onChange={(e) => setConfig({...config, evolution_api_enabled: e.target.checked})}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="evolution_api_enabled" className="ml-2 block text-sm font-medium text-gray-900">
              Habilitar integração com Evolution API
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL da API
              </label>
              <input
                type="text"
                value={config.evolution_api_url}
                onChange={(e) => setConfig({...config, evolution_api_url: e.target.value})}
                placeholder="http://31.97.242.135:8080"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Instância
              </label>
              <input
                type="text"
                value={config.evolution_instance}
                onChange={(e) => setConfig({...config, evolution_instance: e.target.value})}
                placeholder="Teste"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <input
                type="password"
                value={config.evolution_api_key}
                onChange={(e) => setConfig({...config, evolution_api_key: e.target.value})}
                placeholder="Sua API key"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook Chat URL
              </label>
              <input
                type="text"
                value={config.webhook_chat_url}
                onChange={(e) => setConfig({...config, webhook_chat_url: e.target.value})}
                placeholder="URL para receber mensagens do chat"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                URL para onde as mensagens do chat serão enviadas
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4 mt-4">
            <button
              onClick={testConnection}
              disabled={testing}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {testing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              {testing ? 'Testando...' : 'Testar Conexão'}
            </button>
            
            {testResult && (
              <div className={`flex items-center ${
                testResult.success ? 'text-green-600' : 'text-red-600'
              }`}>
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 mr-2" />
                )}
                <span>{testResult.message}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Zap className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-blue-800">Sobre a Evolution API</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p className="mb-2">
                A Evolution API é uma solução para integração com WhatsApp que permite:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Enviar e receber mensagens via WhatsApp</li>
                <li>Gerenciar múltiplas instâncias</li>
                <li>Integrar com seu sistema de chatbot</li>
                <li>Automatizar atendimento ao cliente</li>
              </ul>
              <p className="mt-2">
                Para enviar mensagens, use o endpoint: <code className="bg-blue-100 px-1 py-0.5 rounded">{config.evolution_api_url}/message/sendText/{config.evolution_instance}</code>
              </p>
              <p className="mt-1">
                Formato do payload:
              </p>
              <pre className="bg-blue-100 p-2 rounded mt-1 overflow-x-auto text-xs">
{`{
  "number": "5517991082156",
  "textMessage": {
    "text": "Sua mensagem aqui"
  }
}`}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Logs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Logs de Mensagens</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                // Refresh logs (in a real app, this would fetch from API)
                const savedLogs = localStorage.getItem('evolutionLogs');
                if (savedLogs) {
                  const parsedLogs = JSON.parse(savedLogs);
                  const logsWithDates = parsedLogs.map((log: any) => ({
                    ...log,
                    timestamp: new Date(log.timestamp)
                  }));
                  setLogs(logsWithDates);
                }
              }}
              className="p-2 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100"
              title="Atualizar logs"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button
              onClick={clearLogs}
              className="p-2 text-gray-500 hover:text-red-600 rounded-full hover:bg-gray-100"
              title="Limpar logs"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>

        {logs.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum log encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              Os logs de mensagens aparecerão aqui quando houver atividade
            </p>
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {logs.map((log) => (
              <div 
                key={log.id} 
                className={`p-4 border rounded-lg ${
                  log.status === 'error' 
                    ? 'border-red-200 bg-red-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${
                      log.direction === 'incoming' 
                        ? 'bg-green-100' 
                        : 'bg-blue-100'
                    }`}>
                      {log.direction === 'incoming' ? (
                        <ArrowRight className="h-4 w-4 text-green-600" />
                      ) : (
                        <ArrowRight className="h-4 w-4 text-blue-600 transform rotate-180" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          log.direction === 'incoming' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {log.direction === 'incoming' ? 'Recebida' : 'Enviada'}
                        </span>
                        {log.status === 'error' && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                            Erro
                          </span>
                        )}
                        <div className="ml-2 flex items-center text-xs text-gray-500">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </div>
                      <div className="flex items-center mt-1">
                        <User className="h-3 w-3 text-gray-500 mr-1" />
                        <span className="text-sm text-gray-700">{log.phone}</span>
                      </div>
                      <p className="text-sm text-gray-900 mt-2">{log.message}</p>
                      {log.error && (
                        <p className="text-sm text-red-600 mt-1">{log.error}</p>
                      )}
                    </div>
                  </div>
                  {log.instance && (
                    <span className="text-xs text-gray-500">
                      Instância: {log.instance}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Webhook Format Information */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
        <div className="flex items-start">
          <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              Formato do Webhook da Evolution API
            </h3>
            <p className="text-yellow-700 mb-2">
              O webhook da Evolution API envia dados no seguinte formato:
            </p>
            <pre className="bg-yellow-100 p-3 rounded-lg overflow-x-auto text-xs text-yellow-800">
{`{
  "event": "messages.upsert",
  "instance": "Teste",
  "data": {
    "key": {
      "remoteJid": "5517981425819@s.whatsapp.net",
      "fromMe": false,
      "id": "1D08A24486D0DDB92E4DCB0DB0BA7B1A"
    },
    "pushName": "João Oliveira",
    "message": {
      "conversation": "Mensagem do cliente"
    },
    "messageType": "conversation",
    "messageTimestamp": 1751305148
  },
  "destination": "https://webhook.site/...",
  "date_time": "2025-06-30T14:39:08.467Z",
  "sender": "5517992744174@s.whatsapp.net"
}`}
            </pre>
            <p className="text-yellow-700 mt-4 mb-2">
              Para enviar mensagens, use o seguinte formato:
            </p>
            <pre className="bg-yellow-100 p-3 rounded-lg overflow-x-auto text-xs text-yellow-800">
{`POST ${config.evolution_api_url}/message/sendText/${config.evolution_instance}

{
  "number": "5517991082156",
  "textMessage": {
    "text": "Hello, World!"
  }
}`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Evolution;